// (7) Flip card: front (pills + question + tab content) ↔ back (answer +
// actions, now behind the same tabs — see below). Draggable to ANY sack in
// MasteryBoard.tsx (which renders `data-bin-dropzone` on every sack row
// while a drag is active — collapsed sacks work as drop targets too, same
// as a folder icon in a file manager).
//
// Practice/Theory/Scaffold/Video(/AI Tutor on Pink) are per-card, and follow
// the card across the flip: on the front they're rounded pills (question
// stays centered, only the panel below it swaps); once inverted, the same
// selector re-renders as an underlined tab bar at the top of the back face,
// so Theory/Scaffold/Video/Tutor are still reachable while looking at the
// solution side. The "Practice" tab is special-cased per face: on the front
// it's the due date + "Open card" flip trigger, on the back it's the actual
// answer/reattempt UI that was always there. Clicking anywhere outside the
// card while it's flipped also flips it back — the same "tap away to
// return" behavior the /cards board's expanded modal already has.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useAnimationControls, type PanInfo } from 'framer-motion';
import type { BinColor, TaskCardDTO } from '@/types';
import { BIN_FILL, BIN_LABEL, BIN_ORDER } from '@/lib/bins';
import { MathText } from '@/components/exam/MathText';
import { AITutorPanel } from '@/components/exam/AITutorPanel';
import { WatchVideosButton } from '@/components/video/VideoLibrary';

/** Lower index = better bin (BIN_ORDER is GREEN..PINK, best to worst). */
function isBetterBin(target: BinColor, current: BinColor): boolean {
  return BIN_ORDER.indexOf(target) < BIN_ORDER.indexOf(current);
}

interface TaskCardProps {
  card: TaskCardDTO;
  onMoveToGreen: (cardId: string) => Promise<unknown>;
  onMoveToBin?: (cardId: string, targetBin: BinColor) => Promise<unknown>;
  onReview: (cardId: string, recalled: boolean) => void;
  onReattempt?: (cardId: string, selectedIdx: number) => Promise<{ isCorrect: boolean }>;
  onDragActive?: (active: boolean) => void;
}

const FLIP_EASE = [0.23, 1, 0.32, 1] as const;

type Tab = 'practice' | 'theory' | 'scaffold' | 'video' | 'tutor';
const BASE_TABS: { key: Tab; label: string }[] = [
  { key: 'practice', label: 'Practice' },
  { key: 'theory', label: 'Theory' },
  { key: 'scaffold', label: 'Scaffold' },
  { key: 'video', label: 'Video' },
];

function formatHour(d: Date): string {
  const h = d.getHours();
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${suffix}`;
}

function dueLabel(iso: string): string {
  const due = new Date(iso);
  const diffMs = due.getTime() - Date.now();
  if (diffMs < 0) return 'Overdue';

  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  if (due.toDateString() === today.toDateString()) return `Review due: Today ${formatHour(due)}`;
  if (due.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

  const days = Math.round(diffMs / 86_400_000);
  return `In ${days} days`;
}

export function TaskCard({ card, onMoveToGreen, onMoveToBin, onReview, onReattempt, onDragActive }: TaskCardProps) {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [flipped, setFlipped] = useState(false);
  const [chosenTab, setTab] = useState<Tab>('practice');
  const [expanded, setExpanded] = useState(false);
  const [backExpanded, setBackExpanded] = useState(false);
  const [reattempting, setReattempting] = useState(false);
  const [reattemptResult, setReattemptResult] = useState<'correct' | 'incorrect' | 'error' | null>(null);
  const [upwardMoveHint, setUpwardMoveHint] = useState(false);
  const controls = useAnimationControls();
  const isGreen = card.currentBin === 'GREEN';
  const canReattempt = card.questionType === 'MCQ' && Array.isArray(card.options) && card.options.length > 0;
  // An MCQ attempt is stored as the chosen option's index; a student reads the
  // option, not "1".
  const chosenOption =
    canReattempt && card.submittedAnswer != null && /^\d+$/.test(card.submittedAnswer)
      ? card.options![Number(card.submittedAnswer)]
      : undefined;
  const yourAnswer =
    chosenOption !== undefined
      ? `${String.fromCharCode(65 + Number(card.submittedAnswer))}. ${chosenOption}`
      : card.submittedAnswer;
  const tabs = card.currentBin === 'PINK' ? [...BASE_TABS, { key: 'tutor' as const, label: 'AI Tutor' }] : BASE_TABS;
  // A card that leaves Pink loses its AI Tutor tab; one left open on it would
  // render an empty panel, so the card falls back to Practice.
  const tab: Tab = tabs.some((t) => t.key === chosenTab) ? chosenTab : 'practice';

  // Tap anywhere outside the card flips it back to the front, same "return
  // to the original screen" pattern the /cards board's expanded card uses.
  useEffect(() => {
    if (!flipped) return;
    function handleOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setFlipped(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [flipped]);

  async function handleDragEnd(_e: unknown, info: PanInfo) {
    onDragActive?.(false);
    const el = document.elementFromPoint(info.point.x, info.point.y);
    const zone = el?.closest('[data-bin-dropzone]');
    const targetBin = zone?.getAttribute('data-bin-dropzone') as BinColor | null;
    if (!targetBin || targetBin === card.currentBin) return;

    if (isBetterBin(targetBin, card.currentBin)) {
      // Upward move: has to be earned, not self-reported. Open the card to
      // the reattempt UI (MCQ only); otherwise reject the drop entirely.
      setUpwardMoveHint(true);
      window.setTimeout(() => setUpwardMoveHint(false), 3000);
      if (canReattempt) {
        setTab('practice');
        setFlipped(true);
      } else {
        await controls.start({ x: [0, -8, 8, -6, 6, 0], transition: { duration: 0.3 } });
      }
      return;
    }

    // Same-or-worse bin: still a free self-report.
    try {
      if (targetBin === 'GREEN') await onMoveToGreen(card.id);
      else await onMoveToBin?.(card.id, targetBin);
    } catch {
      // Snap back + shake on failure.
      await controls.start({ x: [0, -8, 8, -6, 6, 0], transition: { duration: 0.3 } });
    }
  }

  async function handleReattempt(selectedIdx: number) {
    if (!onReattempt || reattempting) return;
    setReattempting(true);
    setReattemptResult(null);
    try {
      const result = await onReattempt(card.id, selectedIdx);
      setReattemptResult(result.isCorrect ? 'correct' : 'incorrect');
    } catch {
      setReattemptResult('error');
    } finally {
      setReattempting(false);
    }
  }

  // Theory/Scaffold/Video/Tutor render identically on either face — only
  // "practice" differs (question+due-date on the front, the answer/
  // reattempt UI on the back), so it's handled separately at each call site.
  function renderResourceTab(t: Exclude<Tab, 'practice'>) {
    if (t === 'theory') {
      return card.theoryRef ? (
        <p className="rounded-lg bg-slate-50 p-2.5 text-sm text-slate-700">{card.theoryRef}</p>
      ) : (
        <p className="text-xs text-slate-400">No theory note linked for this one yet.</p>
      );
    }
    if (t === 'scaffold') {
      return (
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate(`/learn/basics/${card.subtopicId}`)}
          className="flex w-full items-center justify-between rounded-lg bg-slate-50 p-2.5 text-left text-sm hover:bg-slate-100"
        >
          <span className="font-medium text-slate-700">{card.subtopicName}</span>
          <span className="text-xs font-semibold text-slate-500">Restart from zero →</span>
        </motion.button>
      );
    }
    if (t === 'video') {
      // Played in the in-app library, never a new tab: a search reference
      // becomes three to five videos the student picks from.
      return card.videoRef ? (
        <WatchVideosButton
          source={{ videoRef: card.videoRef }}
          title={card.subtopicName}
          className="flex w-full items-center justify-between rounded-lg bg-slate-50 p-2.5 text-left text-sm hover:bg-slate-100"
        >
          <span className="font-medium text-slate-700">{card.subtopicName}</span>
          <span className="text-xs font-semibold text-slate-500">Watch ▸</span>
        </WatchVideosButton>
      ) : (
        <p className="text-xs text-slate-400">No video linked for this one yet.</p>
      );
    }
    // tutor
    return card.currentBin === 'PINK' ? (
      <AITutorPanel questionId={card.questionId} questionText={card.questionText} subtopicId={card.subtopicId} />
    ) : null;
  }

  return (
    <motion.div
      ref={rootRef}
      layout
      drag
      dragSnapToOrigin
      onDragStart={() => onDragActive?.(true)}
      onDragEnd={handleDragEnd}
      animate={controls}
      whileDrag={{ scale: 1.03, zIndex: 50, cursor: 'grabbing' }}
      className="relative"
      style={{ perspective: 1200 }}
    >
      <div
        className="relative w-full"
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Inner flip wrapper — front/back are stacked via CSS grid (both
            placed in grid cell 1/1) rather than absolute positioning, so the
            card's height is always the taller of the two faces instead of
            the back face being clipped/scrolled to whatever height the
            front face happens to have. */}
        <motion.div
          className="grid w-full"
          style={{ transformStyle: 'preserve-3d' }}
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ duration: 0.4, ease: FLIP_EASE }}
        >
          {/* FRONT */}
          <div
            className="flex min-h-[220px] flex-col rounded-2xl border-l-4 bg-card p-4 shadow-sm"
            style={{ gridArea: '1 / 1', borderLeftColor: BIN_FILL[card.currentBin], backfaceVisibility: 'hidden' }}
          >
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span
                  className="rounded-full px-2 py-0.5 text-xs font-semibold text-white"
                  style={{ backgroundColor: BIN_FILL[card.currentBin] }}
                >
                  {BIN_LABEL[card.currentBin]}
                </span>
                <span className="text-xs font-medium text-slate-500">
                  {card.earnedMarks}/{card.maxMarks} marks
                </span>
              </div>

              {/* Per-card resource pills — a shared layoutId slides the active
                  highlight between pills instead of just swapping colors. */}
              <div className="flex flex-wrap gap-1.5">
                {tabs.map((t) => {
                  const active = tab === t.key;
                  return (
                    <motion.button
                      key={t.key}
                      onClick={() => setTab(t.key)}
                      whileTap={{ scale: 0.93 }}
                      className="relative overflow-hidden rounded-full px-2.5 py-1 text-fs-11 font-semibold"
                      style={{
                        color: active ? '#fff' : '#475569',
                        border: '1px solid ' + (active ? 'var(--brand-forest)' : '#e2e8f0'),
                      }}
                    >
                      {active && (
                        <motion.span
                          layoutId={`pill-highlight-${card.id}`}
                          className="absolute inset-0"
                          style={{ background: 'var(--brand-forest)' }}
                          transition={{ type: 'spring', stiffness: 500, damping: 34 }}
                        />
                      )}
                      <span className="relative">{t.label}</span>
                    </motion.button>
                  );
                })}
              </div>

              {upwardMoveHint && !canReattempt && (
                <p className="mt-2 rounded-lg bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800">
                  Descriptive questions can't be re-verified here yet — try this sub-topic in Practice Mode instead.
                </p>
              )}
            </div>

            {/* Question — centered in the middle of the card, between the
                pills above and the tab panel below; stays put across every
                pill, only the panel below it changes. */}
            <motion.div layout className="flex flex-1 flex-col items-center justify-center gap-1 py-3 text-center">
              <div className={`text-sm text-ink ${expanded ? '' : 'line-clamp-3'}`}>
                <MathText>{card.questionText || 'Question'}</MathText>
              </div>
              {card.questionText.length > 80 && (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setExpanded((v) => !v)}
                  className="text-xs font-medium text-slate-500 hover:text-ink"
                >
                  {expanded ? 'Show less' : 'read more'}
                </motion.button>
              )}
            </motion.div>

            {/* Tab panel — fades/slides in whenever the selected pill changes. */}
            <div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={tab}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.16 }}
                >
                  {tab === 'practice' ? (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">{dueLabel(card.dueDate)}</span>
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setFlipped(true)}
                        className="text-xs font-semibold text-slate-700 hover:text-ink"
                      >
                        Open card →
                      </motion.button>
                    </div>
                  ) : (
                    renderResourceTab(tab)
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* BACK — the same pills reappear here as an underlined tab bar.
              "Practice" shows the answer/reattempt UI that was always here;
              the other tabs reuse the exact same panels as the front. */}
          <div
            className="rounded-2xl border border-slate-200 bg-card p-4 shadow-sm"
            style={{ gridArea: '1 / 1', backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          >
            <div className="mb-3 flex items-center justify-between gap-3 border-b border-slate-200">
              <div className="flex gap-4">
                {tabs.map((t) => {
                  const active = tab === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setTab(t.key)}
                      className="relative pb-2 text-fs-12 font-semibold transition-colors"
                      style={{ color: active ? 'var(--brand-forest)' : '#64748b' }}
                    >
                      {t.label}
                      {active && (
                        <motion.span
                          layoutId={`tab-underline-${card.id}`}
                          className="absolute inset-x-0 -bottom-px h-[2px] rounded-full"
                          style={{ background: 'var(--brand-forest)' }}
                          transition={{ type: 'spring', stiffness: 500, damping: 34 }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setFlipped(false)}
                className="mb-2 flex-none text-xs text-slate-500 hover:text-ink"
              >
                ← Question
              </motion.button>
            </div>

            {/* The question stays on the opened card, above every tab: the
                answer, the options and the hints mean nothing without it. */}
            <div className="mb-3 rounded-lg bg-slate-50 px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Question</p>
              <div className={`text-sm text-ink ${backExpanded ? '' : 'line-clamp-4'}`}>
                <MathText>{card.questionText || 'Question'}</MathText>
              </div>
              {card.questionText.length > 160 && (
                <button
                  onClick={() => setBackExpanded((v) => !v)}
                  className="mt-0.5 text-xs font-medium text-slate-500 hover:text-ink"
                >
                  {backExpanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.16 }}
              >
                {tab !== 'practice' ? (
                  renderResourceTab(tab)
                ) : (
                  <>
                    {upwardMoveHint && canReattempt && (
                      <p className="mb-2 rounded-lg bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800">
                        Answer it correctly below to move this card up ↓
                      </p>
                    )}

                    <div className="space-y-2 text-sm">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-green-700">Correct answer</p>
                        <div className="text-ink">
                          <MathText>{card.correctAnswer ?? '—'}</MathText>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your answer</p>
                        <div className="text-slate-600">
                          <MathText>{yourAnswer ?? '—'}</MathText>
                        </div>
                      </div>
                      {card.tipsText && (
                        <p className="rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-800">
                          Next time: {card.tipsText}
                        </p>
                      )}
                    </div>

                    {/* One-click MCQ reattempt */}
                    {canReattempt && onReattempt && (
                      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Reattempt — pick an option
                        </p>
                        <div className="flex flex-col gap-1.5">
                          {card.options!.map((opt, i) => (
                            <motion.button
                              key={i}
                              whileTap={{ scale: 0.97 }}
                              disabled={reattempting}
                              onClick={() => handleReattempt(i)}
                              className="rounded-lg border border-slate-200 bg-card px-3 py-1.5 text-left text-sm hover:border-slate-400 disabled:opacity-50"
                            >
                              <MathText>{opt}</MathText>
                            </motion.button>
                          ))}
                        </div>
                        <AnimatePresence>
                          {reattemptResult === 'correct' && (
                            <motion.p
                              initial={{ opacity: 0, scale: 0.9, y: -4 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              className="mt-1.5 text-xs font-semibold text-green-700"
                            >
                              Correct! Moving to Green…
                            </motion.p>
                          )}
                          {reattemptResult === 'incorrect' && (
                            <motion.p
                              initial={{ opacity: 0, scale: 0.9, y: -4 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              className="mt-1.5 text-xs font-semibold text-amber-700"
                            >
                              Not quite — this stays in Pink for now.
                            </motion.p>
                          )}
                          {reattemptResult === 'error' && (
                            <motion.p
                              initial={{ opacity: 0, scale: 0.9, y: -4 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              className="mt-1.5 text-xs font-semibold text-red-600"
                            >
                              Couldn't submit — please try again.
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </div>
                    )}

                    {!isGreen && (
                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={() => onMoveToGreen(card.id)}
                        className="mt-3 w-full rounded-xl px-4 py-2 text-sm font-semibold text-white"
                        style={{ backgroundColor: BIN_FILL.GREEN }}
                      >
                        Move to Green ✓
                      </motion.button>
                    )}

                    <div className="mt-2 flex gap-2">
                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={() => onReview(card.id, true)}
                        className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        I recalled it
                      </motion.button>
                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={() => onReview(card.id, false)}
                        className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        I forgot it
                      </motion.button>
                    </div>
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
